// Palindromes — full course content for the DSA Explanation Modal.
// Java-focused. Same section-course schema as matrix.js: concept → internals →
// applied templates → errors → complexity → beginner drills → cheatsheet → playground.
// Reuses the existing 'window-diagram' section type (introduced for Sliding Window) for its visual.

import palindromeDrills from './palindromeDrills';
const drill = id => palindromeDrills.find(d => d.id === id);

const palindromes = {
  key: 'Palindromes',
  tagline: 'A string that reads the same forwards and backwards — checked from the outside in, or grown from the middle out.',

  sections: [
    {
      id: 'intro',
      title: 'Introduction to Palindromes',
      type: 'text',
      body: [
        "A palindrome is a sequence that reads identically forwards and backwards — \"racecar\", \"level\", or (ignoring spaces and punctuation) \"a man, a plan, a canal: panama.\" Palindrome problems are really about two things: VALIDATING whether a given sequence already is one (checking from the outside in), and FINDING or COUNTING palindromic pieces within a larger string (growing outward from the middle).",
        "Nearly every problem in this pattern is built from one of exactly two techniques: two pointers closing inward from both ends, or expanding outward from a chosen center. Recognizing which one a problem calls for — validate, or find/count — is most of the battle.",
      ],
    },
    {
      id: 'why',
      title: 'Why the Palindromes Pattern?',
      type: 'text',
      body: [
        "The naive way to check whether every substring of a string is a palindrome is to check each one independently, character by character — O(n) work for each of O(n²) substrings, giving O(n³) overall. Both core techniques in this pattern beat that by reusing structure: two pointers validate a single string in one O(n) pass, and expanding outward from a center reuses the fact that a longer palindrome always contains a shorter one at its exact same center.",
        "The center-expansion insight specifically is what turns \"check every substring separately\" into \"try every possible center, and only ever look outward\" — collapsing the cubic brute force down to O(n²), without ever needing extra memory to remember earlier results.",
      ],
    },
    {
      id: 'analogy',
      title: 'How Palindromes Work — Two Real-World Pictures',
      type: 'text',
      heading: 'A Word Held Up to a Mirror, and Ripples From a Dropped Stone',
      body: [
        "Picture holding a word up to a mirror: a true palindrome looks EXACTLY the same in the reflection as it does directly. Checking this by hand means comparing the first letter to the last, the second to the second-to-last, and so on, walking inward from both ends until the two comparing fingers meet in the middle — that's precisely the two-pointer validation technique.",
        "Now picture dropping a stone into still water and watching the ripple expand outward in a perfect circle. A palindrome centered at some point in a string behaves the same way: start at the center (a single letter, or the gap between two letters) and let the \"ripple\" expand outward — as long as the characters on both sides keep matching, the ripple keeps growing. The moment they stop matching, the ripple has reached its true edge, and that's the palindrome's full extent.",
      ],
    },
    {
      id: 'visualize',
      title: 'Visualizing Expand-Around-Center',
      type: 'window-diagram',
      body: [
        "This shows the \"ripple\" fully expanded from the center between index 1 and index 1 in \"babad\" — the palindrome \"bab\" spans indices 0 through 2, highlighted as the current window.",
        "Expansion stops the instant the characters on either side stop matching (or a boundary is hit) — at that point, the window shown here is the palindrome's full, final extent for this particular center.",
      ],
      demo: {
        values: ['b', 'a', 'b', 'a', 'd'],
        left: 0,
        right: 2,
        label: 'Expanded from center index 1 — the palindrome "bab" (indices 0–2).',
      },
    },
    {
      id: 'template',
      title: 'The Universal Expand-Around-Center Recipe',
      type: 'code',
      code:
`int expandAroundCenter(String s, int left, int right) {
    while (left >= 0 && right < s.length() && s.charAt(left) == s.charAt(right)) {
        left--;
        right++;
    }
    return right - left - 1;   // the length of the palindrome found
}

// Try EVERY possible center — 2n - 1 of them in a string of length n:
for (int center = 0; center < s.length(); center++) {
    expandAroundCenter(s, center, center);       // odd-length palindromes
    expandAroundCenter(s, center, center + 1);   // even-length palindromes
}`,
      note: 'Every center-expansion problem in this pattern — Longest Palindromic Substring, Palindromic Substrings, even parts of Palindrome Partitioning — is a variation on trying both an odd-length and an even-length center at every position, and reacting to what each expansion finds.',
    },
    {
      id: 'countingtemplate',
      title: 'Applied: Counting Instead of Tracking a Maximum',
      type: 'code',
      code:
`int countSubstrings(String s) {
    int count = 0;
    for (int center = 0; center < s.length(); center++) {
        count += expandAndCount(s, center, center);
        count += expandAndCount(s, center, center + 1);
    }
    return count;
}

private int expandAndCount(String s, int left, int right) {
    int count = 0;
    while (left >= 0 && right < s.length() && s.charAt(left) == s.charAt(right)) {
        count++;   // every successful expansion IS a distinct palindromic substring
        left--;
        right++;
    }
    return count;
}`,
      note: 'The exact same expansion loop as the template above — the only change is WHAT the loop body does with each successful expansion. Tracking "the longest one" gives Longest Palindromic Substring; counting "every one" gives Palindromic Substrings.',
    },
    {
      id: 'twopointertemplate',
      title: 'Applied: Two-Pointer Validation',
      type: 'code',
      code:
`boolean isPalindrome(String s) {
    int left = 0, right = s.length() - 1;
    while (left < right) {
        if (s.charAt(left) != s.charAt(right)) {
            return false;
        }
        left++;
        right--;
    }
    return true;
}`,
      note: 'This is the OTHER half of the pattern — validating a single known string, rather than searching for palindromes within a larger one. Valid Palindrome, Valid Palindrome II, and Palindrome Number all build on this exact two-pointer shape.',
    },
    {
      id: 'errors',
      title: 'Common Errors',
      type: 'errors',
      items: [
        {
          mistake: 'Forgetting the even-length center case',
          why: 'A palindrome like "abba" has no single central CHARACTER — its center is the gap between the two middle characters. Only trying single-character centers (center, center) silently misses every even-length palindrome in the string.',
          fix: 'Always try BOTH expandAroundCenter(s, center, center) (odd length) and expandAroundCenter(s, center, center + 1) (even length) at every position.',
        },
        {
          mistake: 'Using strict > instead of >= (or vice versa) when tracking the best result',
          why: 'When two palindromes tie for the longest length, using len >= maxLen instead of len > maxLen changes WHICH one gets returned — not wrong per the problem statement (multiple valid answers are usually accepted), but inconsistent with what a specific test might expect.',
          fix: 'Be deliberate about tie-breaking: len > maxLen keeps the FIRST longest palindrome found; len >= maxLen keeps the LAST. Either is usually valid, but pick one intentionally.',
        },
        {
          mistake: 'Confusing "distinct substrings" with "distinct text"',
          why: 'In Palindromic Substrings, "aaa" contains six palindromic substrings — but only two DIFFERENT strings of text ("a" and "aa" and "aaa" — three, appearing at different positions). Substrings at different positions count separately even if their characters are identical.',
          fix: 'Count every successful expansion as one substring, regardless of whether its text duplicates an earlier one — position matters, content alone does not.',
        },
        {
          mistake: 'Not handling non-alphanumeric characters or case sensitivity in Valid Palindrome-style problems',
          why: 'Problems like Valid Palindrome explicitly ignore punctuation, spaces, and letter case — a naive character-by-character comparison without filtering and lowercasing will reject genuinely valid palindromes like "A man, a plan, a canal: Panama."',
          fix: 'Explicitly check Character.isLetterOrDigit() before comparing, and compare with Character.toLowerCase() applied to both sides.',
        },
      ],
    },
    {
      id: 'complexity',
      title: 'Time Complexity',
      type: 'complexity',
      rows: [
        { op: 'Two-pointer validation (Valid Palindrome)', time: 'O(n)', note: 'A single inward pass, O(1) extra space.' },
        { op: 'Expand around center (Longest Palindromic Substring, Palindromic Substrings)', time: 'O(n²)', note: '2n-1 centers, each expanding up to O(n) in the worst case.' },
        { op: 'Manacher\'s Algorithm', time: 'O(n)', note: 'Reuses previously-found palindrome information via mirroring — true linear time.' },
        { op: 'Longest Palindromic Subsequence, Min-Cut Partitioning (2-D DP)', time: 'O(n²)', note: 'A grid of subproblems, each combining a small number of neighbors.' },
        { op: 'Shortest Palindrome (KMP failure function)', time: 'O(n)', note: 'One pass over s + separator + reverse(s).' },
      ],
    },
    {
      id: 'when',
      title: 'When to Use',
      type: 'text',
      heading: 'Reach for Palindrome techniques when you see…',
      body: [
        'A request to check whether a GIVEN string is a palindrome — two pointers closing inward, O(n), is almost always the answer.',
        'A request to find or count palindromic SUBSTRINGS within a larger string — expand around every possible center, O(n²) (or O(n) with Manacher\'s for the truly optimal version).',
        'A palindrome question that also involves SUBSEQUENCES (not required to be contiguous) rather than substrings — that\'s a strong signal for 2-D DP instead of center-expansion.',
        'A request allowing a small number of edits (one deletion, a few character changes) before the palindrome check — usually solvable by branching at the FIRST mismatch a two-pointer scan finds, rather than trying every possible edit position.',
        'A palindrome question phrased in terms of REARRANGING characters (not reading the string as given) — check character frequency parity (at most one odd count) rather than trying actual permutations.',
      ],
    },
    {
      id: 'problems',
      title: 'Beginner Problems (Easy → Medium → Advanced)',
      type: 'problems',
      groups: [
        {
          level: 'Easy',
          items: [
            drill('valid-palindrome'),
            drill('longest-palindromic-substring-drill'),
            drill('palindrome-number'),
          ],
        },
        {
          level: 'Medium',
          items: [
            drill('palindromic-substrings-drill'),
            drill('valid-palindrome-ii'),
            drill('longest-palindromic-subsequence'),
          ],
        },
        {
          level: 'Advanced',
          items: [
            drill('palindrome-partitioning-min-cuts'),
            drill('shortest-palindrome'),
            drill('palindrome-permutation'),
          ],
        },
      ],
      note: 'These are foundation drills to build comfort with both core techniques — inward two-pointer validation, and outward center-expansion — before moving into the curated interview problem list for this pattern.',
    },
    {
      id: 'cheatsheet',
      title: 'Cheatsheet & Recap',
      type: 'cheatsheet',
      items: [
        'Two techniques cover almost everything here: two pointers closing inward (VALIDATE a given string), and expanding outward from a center (FIND or COUNT palindromes within a larger string).',
        'Always try both an odd-length center (single character) and an even-length center (gap between two characters) — missing either silently drops half of all possible palindromes.',
        'A palindrome SUBSTRING must be contiguous; a palindrome SUBSEQUENCE doesn\'t need to be — the second requires 2-D DP, not center-expansion.',
        'For "allow up to one deletion" problems, branch only at the FIRST mismatch a two-pointer scan finds — no need to try every possible deletion position.',
        'A permutation-based palindrome question ("can this be rearranged into one?") reduces to counting character frequency parity: at most one odd count.',
        'Manacher\'s Algorithm and the KMP failure-function trick (for Shortest Palindrome) both push center-expansion-style problems from O(n²) down to O(n) — worth mentioning as follow-ups once the standard solution works.',
      ],
    },
    {
      id: 'playground',
      title: 'Interactive Practice & Quiz',
      type: 'playground',
    },
  ],
};

export default palindromes;
